import React, { useState, useEffect } from 'react';
import { Brain, Activity, Target, Users, Compass, Shield, AlertTriangle,
         RefreshCw, Printer, ChevronRight, Play, FileText, ArrowLeft,
         CheckCircle, XCircle, BookOpen, Link } from 'lucide-react';
// The Analyzer no longer stores anything. It has no login, so a rule could not
// be scoped to anyone: `allow read, write: if true` was the only rule that let
// it work, which left every patient's name, date of birth, healthcare number
// and 39 answers readable by anyone holding the project id.
//
// It does not need to store them. The assessment code carries the whole reading
// to NM Rebuild, which keeps it scoped to the owning clinician. One record, in
// the place built to hold it. Dr. Oyelese, 2026-09-26.
import {
  SECTIONS, ALL_ITEMS, analyse, DEMOS, demoAnswers, encodeAnswers,
  rebuildPayload, REBUILD_CONTRACT_VERSION
} from './nmf';

// ============================================
// NM ANALYZER v4
// Deterministic. No AI. Same answers in, same report out, every time.
// Eight sections, 39 binary items. Classification, not score.
// ============================================

const REBUILD_API_URL = 'https://us-central1-nm-rebuild.cloudfunctions.net/logActivity';
const REBUILD_RECOMMEND_URL = 'https://us-central1-nm-rebuild.cloudfunctions.net/recommendPhase';
const REBUILD_HOME = 'https://rebuild.navigationalmind.com/clinician';

let rebuildPatientId = null;

const parseRebuildToken = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('rebuild_token');
    if (token) {
      const decoded = JSON.parse(atob(token));
      rebuildPatientId = decoded.patientId;
      return { token, patientId: decoded.patientId, tool: decoded.tool,
               timestamp: decoded.timestamp, patientInfo: decoded.patientInfo || null };
    }
  } catch (e) {
    console.error('Error parsing rebuild token:', e);
  }
  return null;
};

// ============================================
// THE REBUILD CONTRACT — v4
// This is the whole of what nm-analyzer publishes. nm-rebuild is retrofitted
// to read it; nothing here is shaped by what the old Port happened to expect.
// Keys are words, never a letter and a number: S1-S8 are the stations,
// G1-G5 the Gates, R1-R7 the Residents, and none of those are sections.
// ============================================

// The contract now lives in the engine (nmf.js) so the Analyzer and Rebuild
// build it from one definition and cannot drift apart.

const reportToRebuild = async (rebuildContext, event, data) => {
  if (!rebuildContext) return;
  try {
    await fetch(REBUILD_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patientId: rebuildContext.patientId,
        tool: 'nm-analyzer',
        event,
        data,
        timestamp: new Date().toISOString()
      })
    });
  } catch (e) {
    console.error('Failed to report to Rebuild:', e);
  }
};

// The recommendPhase endpoint destructures its fields off the top level of the
// body — nesting them under `analysisData` delivers undefined to every one.
// The payload is therefore spread flat here, not passed as an object.
const recommendPhaseChange = async (rebuildContext, recommendedPhase, reason, payload) => {
  if (!rebuildContext) return;
  try {
    await fetch(REBUILD_RECOMMEND_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patientId: rebuildContext.patientId,
        recommendedPhase,
        reason,
        source: 'nm-analyzer',
        ...payload,
        timestamp: new Date().toISOString()
      })
    });
  } catch (e) {
    console.error('Failed to send phase recommendation:', e);
  }
};

const returnToRebuild = () => {
  if (rebuildPatientId) localStorage.setItem('returnToPatient', rebuildPatientId);
  window.location.href = REBUILD_HOME;
};

// Rebuild's phase slugs. A value, not a key — the retrofit may replace these.
const PHASE_SLUG = {
  'STABILIZATION': 'stabilization',
  'SOFT ARTICULATION': 'soft_articulation',
  'DIRECTION RECOVERY': 'direction_recovery',
  'PROCESS RESUMPTION': 'process_resumption'
};

const PHASE_REASON = {
  'STABILIZATION': 'A section is RED, or a sentinel item fired — stabilization first',
  'SOFT ARTICULATION': 'Every section carrying concern is YELLOW — soft articulation',
  'DIRECTION RECOVERY': 'Concern is partial, some sections clear — direction recovering',
  'PROCESS RESUMPTION': 'All eight sections GREEN — ready for process resumption'
};

// ============================================
// SESSION TIMELINE
// The old chart plotted a 0-10 navigability score. This instrument produces no
// score, so there is nothing to plot: it shows the eight section statuses as a
// strip, one column per session, which is movement without an invented number.
// ============================================


const Dot = ({ s }) => <span className={`dot ${s}`} />;

// ============================================
// APP
// ============================================

function App() {
  const [view, setView] = useState('landing');   // landing · about · demo · input · report
  const [mode, setMode] = useState(null);        // 'demo' | 'clinical'
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [patientInfo, setPatientInfo] = useState({ surname: '', firstName: '', dob: '', sex: '' });
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [errors, setErrors] = useState({});
  const [rebuildContext, setRebuildContext] = useState(null);

  useEffect(() => {
    const context = parseRebuildToken();
    if (context) {
      setRebuildContext(context);
      if (context.patientInfo) {
        // A token minted before this change carries a single `name`; split it
        // on the last space so an older link still fills the form sensibly.
        const pi = context.patientInfo;
        const legacy = (pi.name || '').trim();
        const cut = legacy.lastIndexOf(' ');
        setPatientInfo({
          surname:   pi.surname   || (cut > 0 ? legacy.slice(cut + 1) : legacy),
          firstName: pi.firstName || (cut > 0 ? legacy.slice(0, cut) : ''),
          dob: pi.dob || '',
          sex: pi.sex || ''
        });
        setMode('clinical');
        setView('input');
      }
    }
  }, []);

  const handlePatientInfoChange = (f, v) => {
    setPatientInfo(prev => ({ ...prev, [f]: v }));
    if (errors[f]) setErrors(prev => ({ ...prev, [f]: null }));
  };

  // Answers are the strings 'yes' and 'no' — the same values the engine
  // compares against each item's concerning direction.
  const handleAnswerChange = (id, v) => {
    setAnswers(prev => ({ ...prev, [id]: v }));
    if (errors.items) setErrors(prev => ({ ...prev, items: null }));
  };

  /* Three distinct actions, because one was doing all three badly.

     NEW      clears the answers and the report and returns to the form. It
              KEEPS the patient, because the next thing you do is assess the
              same person again or correct a mis-entry — and because when the
              Analyzer was opened from Rebuild the patient fields are locked,
              so clearing them left a form that could not be filled or edited.

     RESET    used by Back and Cancel. Returns to the landing page. Keeps the
              patient when it came from Rebuild: that identity belongs to the
              session, not to one assessment.

     EXIT     leaves. Back to Rebuild when opened from there, otherwise to the
              landing page. Previously the only way out sat beside a bare
              refresh icon in the header, which is how a New became a sign-in
              page. Dr. Oyelese, 2026-09-28. */

  const blankPatient = { surname: '', firstName: '', dob: '', sex: '' };

  const startNew = () => {
    setAnswers({}); setReport(null); setErrors({}); setSelectedDemo(null);
    if (mode === 'demo') { setView('demo'); return; }
    setMode('clinical'); setView('input');
    window.scrollTo(0, 0);
  };

  const reset = () => {
    setView('landing'); setMode(null); setSelectedDemo(null);
    if (!rebuildContext?.patientInfo) setPatientInfo(blankPatient);
    setAnswers({}); setReport(null); setErrors({});
  };

  const exitAnalyzer = () => {
    if (rebuildContext) { returnToRebuild(); return; }
    setPatientInfo(blankPatient);
    reset();
  };

  const startDemo = () => { setMode('demo'); setView('demo'); };
  const startClinical = () => { setMode('clinical'); setView('input'); };

  const selectDemo = (demo) => {
    const a = demoAnswers(demo.id);
    setSelectedDemo(demo);
    setAnswers(a);
    processReport(a, true);
  };

  // Decision 3: block the report until every one of the 39 is answered.
  const validate = () => {
    const e = {};
    if (!patientInfo.surname.trim()) e.surname = 'Surname required';
    if (!patientInfo.firstName.trim()) e.firstName = 'First name required';
    if (!patientInfo.dob) e.dob = 'Date of birth required';
    if (!patientInfo.sex) e.sex = 'Sex required';
    const missing = ALL_ITEMS.filter(i => answers[i.id] === undefined);
    if (missing.length) e.items = `All ${ALL_ITEMS.length} items must be answered — ${missing.length} remaining`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };



  const processReport = (a, isDemo = false) => {
    const r = analyse(a);
    if (!r.complete) {
      setErrors({ items: `All ${ALL_ITEMS.length} items must be answered — ${r.missing.length} remaining` });
      return;
    }

    const assessmentId = Date.now();

    // The whole assessment as eight characters. Every item is binary, so the
    // 39 answers are 39 bits; a receiver running this same engine reproduces
    // this reading exactly rather than copying it.
    let assessmentCode = null;
    try { assessmentCode = encodeAnswers(a); }
    catch (e) { console.error('Could not encode the assessment:', e); }

    const reportData = {
      assessmentCode,
      contract: REBUILD_CONTRACT_VERSION,
      timestamp: assessmentId,
      patientInfo: isDemo
        ? { surname: 'Demo', firstName: '', dob: '', sex: '' }
        : patientInfo,
      answers: a,
      // the analysis, stored whole so a saved session can be re-read without
      // re-running the engine, and so the timeline has a status row to read
      sectionProfile: Object.fromEntries(
        Object.entries(r.sections).map(([k, v]) => [k, v.status])),
      analysis: r,
    };

    setReport(reportData);
    setView('report');

    if (!isDemo) {
      const payload = rebuildPayload(r, assessmentId, assessmentCode);
      reportToRebuild(rebuildContext, 'assessment_completed', payload);
      recommendPhaseChange(rebuildContext,
        PHASE_SLUG[r.phase] || 'stabilization',
        PHASE_REASON[r.phase] || 'Phase determination required',
        payload);
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;
    processReport(answers, false);
  };

  /* Print into a window whose entire content IS the report, rather than asking
     the browser to hide the application around it with @media print.

     Why this rather than a fix to the print stylesheet: the blank output could
     not be reproduced here, so the cause is not established. A tab opened by
     window.open from another site, a stylesheet that had not finished applying,
     or a print rule interacting with the app shell would each produce it. This
     approach is independent of all three, because there is no app shell in the
     window being printed. It is what NM Rebuild already does, for the same
     reason. */
  const handlePrint = () => {
    const node = document.querySelector('.report-container');
    if (!node) { window.print(); return; }

    const clone = node.cloneNode(true);
    clone.querySelectorAll('.no-print').forEach(el => el.remove());

    const who = (report?.patientInfo?.surname || 'Report').replace(/[<>]/g, '');
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) { window.print(); return; }   // pop-up blocked: fall back

    w.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>NM Analysis Report — ${who}</title>
      <style>
        * { box-sizing: border-box; }
        body { font: 11pt/1.55 ui-sans-serif, -apple-system, "Segoe UI", sans-serif;
               color: #111827; margin: 0; padding: 0; }
        h1 { font-size: 17pt; margin: 0 0 2pt; }
        h2 { font-size: 12pt; margin: 14pt 0 4pt; border-top: 1px solid #d1d5db; padding-top: 6pt; }
        h4 { font-size: 11pt; margin: 8pt 0 2pt; }
        p, li, td, th { font-size: 10pt; }
        table { border-collapse: collapse; width: 100%; margin: 4pt 0; }
        th, td { text-align: left; padding: 3pt 6pt; border-bottom: 1px solid #e5e7eb; }
        th { color: #6b7280; font-weight: 600; }
        thead { display: table-header-group; }
        ul { margin: 2pt 0 6pt 16pt; padding: 0; }
        .dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%;
               margin-right: 6px; vertical-align: middle;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .dot.GREEN { background: #166534; } .dot.YELLOW { background: #a16207; }
        .dot.RED { background: #991b1b; }
        .note, .code-note { color: #6b7280; font-size: 9pt; }
        .assessment-code { border: 1px dashed #9ca3af; padding: 8pt 10pt; margin: 6pt 0; }
        .code-value { font-family: ui-monospace, Menlo, monospace; font-size: 14pt;
                      letter-spacing: 0.12em; }
        .annotation { border-left: 3px solid #9ca3af; padding: 4pt 0 4pt 8pt; margin: 4pt 0; }
        tr, li, .breakdown, .annotation { break-inside: avoid; }
        @page { margin: 15mm; }
      </style></head><body>${clone.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);   // let the new document lay out first
  };

  // ============================================
  // RENDER
  // ============================================

  const renderLanding = () => (
    <div className="landing-view">
      {rebuildContext && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div className="rebuild-badge"><Link size={16} /><span>Connected to Rebuild</span></div>
          <button onClick={returnToRebuild} className="rebuild-return-btn">← Return to Rebuild</button>
        </div>
      )}
      <div className="landing-content">
        <div className="landing-hero">
          <Brain size={64} className="hero-icon" />
          <h1>NM Analyzer</h1>
          <p className="hero-subtitle">Navigational Mind Analysis Tool</p>
          <p className="hero-description">
            Detect the state of the Mind Machine. The Analyzer tells you <em>where</em> the
            system is — not what to do about it.
          </p>
        </div>

        <div className="landing-modes three-col">
          <div className="mode-card about-card" onClick={() => setView('about')}>
            <BookOpen size={32} className="mode-icon" />
            <h2>About</h2>
            <p>The eight sections, what each one reads, and what the instrument will not tell you.</p>
            <span className="mode-cta">Read more <ChevronRight size={16} /></span>
          </div>

          <div className="mode-card" onClick={startDemo}>
            <Play size={32} className="mode-icon" />
            <h2>Try Demo</h2>
            <p>Eight worked profiles, answered for you, straight to the report.</p>
            <span className="mode-cta">No login required <ChevronRight size={16} /></span>
          </div>

          <div className="mode-card" onClick={startClinical}>
            <FileText size={32} className="mode-icon" />
            <h2>Clinical Assessment</h2>
            <p>All {ALL_ITEMS.length} items. The report carries an assessment code for NM Rebuild.</p>
            <span className="mode-cta">Start assessment <ChevronRight size={16} /></span>
          </div>
        </div>

        {/* What this is — a <details> dropdown, so it needs no JavaScript and
            works before anything else on the page has loaded. Closed by
            default: it is for the reader who does not yet know what any of
            this is, and should not detain the one who does. */}
        <details className="explainer">
          <summary>What is the NM Analyzer?</summary>
          <div className="explainer-body">
            <p>
              A clinical instrument that reads <em>where a person is</em> — not what is
              wrong with them — and does so the same way every time.
            </p>
            <p>
              Thirty-nine questions, each answered yes or no, across eight sections.
              It is <strong>deterministic</strong>: the same answers produce the same
              reading, with no AI involved at any point. It <strong>classifies and does
              not score</strong>, because variation lies in which parts are down, not
              how far.
            </p>
            <p>
              It does not establish cause, duration, mood or diagnosis — and the report
              says so on its own face.
            </p>
            <a className="explainer-link" href="/NM-Analyzer.pdf" target="_blank" rel="noopener noreferrer">
              Read the full explanation (PDF, 2 pages)
            </a>
            <a className="explainer-link secondary" href="/NM-Analyzer.pdf" download="NM-Analyzer.pdf">
              Download it
            </a>
          </div>
        </details>

        <div className="landing-footer">
          <p>© 2026 Dr. Olutoyese Oyelese • Navigational Mind Architecture</p>
        </div>
      </div>
    </div>
  );

  const renderAbout = () => (
    <div className="about-view">
      <div className="about-container">
        <button className="back-btn" onClick={reset}><ArrowLeft size={20} /> Back</button>

        <div className="about-header">
          <Brain size={48} className="about-icon" />
          <h1>About NM Analyzer</h1>
          <p className="about-subtitle">Clinical Manual for State Detection</p>
        </div>

        <section className="about-section disclaimer">
          <h2>Clinical Disclaimer</h2>
          <div className="about-content">
            <p>
              The NM Analyzer is a <strong>clinical operationalization</strong> of the
              Navigational Mind Framework. The sections, the status thresholds and the phase
              triggers are derived from the framework's structural logic. They are not
              directly prescribed by the source texts.
            </p>
            <p>
              This tool has not been empirically validated. It is a clinician-facing
              instrument designed to support — not replace — clinical judgment.
            </p>
          </div>
        </section>

        <section className="about-section">
          <h2>What it is</h2>
          <div className="about-content">
            <p>
              A <strong>state detection instrument</strong>. {ALL_ITEMS.length} items, each
              answered yes or no, across eight sections. It is <strong>deterministic</strong>:
              the same answers produce the same report every time. No AI is involved at any
              point, and nothing is inferred that is not written into the rules.
            </p>
            <p className="about-emphasis">
              The person is treated as a living navigation system rather than a collection
              of symptoms.
            </p>
          </div>
        </section>

        <section className="about-section">
          <h2>It classifies. It does not score.</h2>
          <div className="about-content">
            <p>
              There is no total, no index, no 0–10. The framework holds that variation lies
              in <em>which</em> parts are down, not how far — so a number that averaged them
              would destroy the reading it was meant to summarise. Every item is binary
              because each foundational process is binary at its core; complexity arises from
              the number of processes involved, not from gradation within one.
            </p>
          </div>
        </section>

        <section className="about-section">
          <h2>The eight sections</h2>
          <div className="gates-grid">
            {SECTIONS.map((s, n) => (
              <div className="gate-card" key={s.id}>
                <div className="gate-number">{n + 1}</div>
                <div className="gate-info">
                  <h3>{s.n}</h3>
                  <p>{s.goal}</p>
                  <p className="gate-count">{s.items.length} items</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about-section">
          <h2>What it will not tell you</h2>
          <div className="about-content about-not-list">
            <div className="not-item"><XCircle size={20} /> <strong>Cause.</strong> No item asks why, and no reading implies one.</div>
            <div className="not-item"><XCircle size={20} /> <strong>Duration.</strong> One session reads a state, not a trajectory.</div>
            <div className="not-item"><XCircle size={20} /> <strong>Mood.</strong> No item asks about sadness or anhedonia.</div>
            <div className="not-item"><XCircle size={20} /> <strong>Diagnosis.</strong> It is not a symptom checklist and does not produce one.</div>
          </div>
          <p className="about-core-statement">
            "The question is never 'what should this person do?' The question is:
            <em> what is this system capable of doing without harm?</em>"
          </p>
        </section>

        <section className="about-section">
          <h2>Governance principles</h2>
          <div className="principles-grid">
            <div className="principle-card">
              <Shield size={24} /><h3>State Before Strategy</h3>
              <p>Detect where the system is before deciding what to do.</p>
            </div>
            <div className="principle-card">
              <Activity size={24} /><h3>Capacity Before Complexity</h3>
              <p>The system must be stable before complex work begins. Sequence matters.</p>
            </div>
            <div className="principle-card">
              <Target size={24} /><h3>Repair Classes, Not Techniques</h3>
              <p>The analyzer identifies what class of repair is legitimate, not which technique.</p>
            </div>
            <div className="principle-card">
              <AlertTriangle size={24} /><h3>No False Repair</h3>
              <p>A powerful tool becomes harmful when deployed against the wrong state.</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>BOF: discipline against confabulation</h2>
          <div className="about-content">
            <p>
              The Binary Outcome Framework is <strong>not a calculation engine</strong>. It is
              a discipline against confabulation — it prevents the system from inventing
              certainty where none exists. At its core it describes the relationship with
              change as P(D) against P(Ur+ir), which is itself binary.
            </p>
          </div>
        </section>

        <div className="about-actions">
          <button className="primary-btn" onClick={startDemo}><Play size={20} /> Try Demo</button>
          <button className="secondary-btn" onClick={startClinical}><FileText size={20} /> Start Clinical Assessment</button>
        </div>

        <footer className="about-footer">
          <p>NM Analyzer v4 — Navigational Mind Architecture</p>
          <p>© 2026 Dr. Olutoyese Oyelese</p>
        </footer>
      </div>
    </div>
  );

  const renderDemo = () => (
    <div className="demo-view">
      <div className="demo-header">
        <button className="back-btn" onClick={reset}><ArrowLeft size={20} /> Back</button>
        <h2>Worked Profiles</h2>
        <p>Each one fills all {ALL_ITEMS.length} items and goes straight to the report.</p>
      </div>
      <div className="scenario-grid">
        {DEMOS.map(d => (
          <div key={d.id}
               className={`scenario-card ${selectedDemo?.id === d.id ? 'selected' : ''}`}
               onClick={() => selectDemo(d)}>
            <h3>{d.n}</h3>
            <p>{d.d}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderInput = () => {
    const answered = ALL_ITEMS.filter(i => answers[i.id] !== undefined).length;
    return (
      <div className="input-view">
        <div className="form-container">
          {Object.keys(errors).length > 0 && (
            <div className="error-banner">
              <AlertTriangle size={20} />
              <span>{errors.items || 'Please complete all required fields'}</span>
            </div>
          )}

          <section className="form-section">
            <h2 className="section-title"><Users size={20} /> Patient Information</h2>
            {rebuildContext?.patientInfo && (
              <div className="rebuild-loaded">
                <CheckCircle size={16} /><span>Patient information loaded from Rebuild</span>
              </div>
            )}
            <div className="form-grid">
              <div className="form-field">
                <label>Surname *</label>
                <input type="text" value={patientInfo.surname}
                       onChange={e => handlePatientInfoChange('surname', e.target.value)}
                       className={errors.surname ? 'error' : ''}
                       readOnly={!!rebuildContext?.patientInfo?.surname} />
              </div>
              <div className="form-field">
                <label>First Name *</label>
                <input type="text" value={patientInfo.firstName}
                       onChange={e => handlePatientInfoChange('firstName', e.target.value)}
                       className={errors.firstName ? 'error' : ''}
                       readOnly={!!rebuildContext?.patientInfo?.firstName} />
              </div>
              <div className="form-field">
                <label>Date of Birth *</label>
                <input type="date" value={patientInfo.dob}
                       onChange={e => handlePatientInfoChange('dob', e.target.value)}
                       className={errors.dob ? 'error' : ''}
                       readOnly={!!rebuildContext?.patientInfo?.dob} />
              </div>
              <div className="form-field">
                <label>Sex *</label>
                <select value={patientInfo.sex}
                        onChange={e => handlePatientInfoChange('sex', e.target.value)}
                        className={errors.sex ? 'error' : ''}
                        disabled={!!rebuildContext?.patientInfo?.sex}>
                  <option value="">Select...</option>
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </section>

          {SECTIONS.map((s, n) => (
            <section className="form-section" key={s.id}>
              <h2 className="section-title">
                <Compass size={20} /> {s.n}
                <span className="section-count">Section {n + 1} · {s.items.length} items</span>
              </h2>
              <p className="section-subtitle">{s.goal}</p>
              {s.stem && <p className="section-stem">{s.stem}</p>}
              {s.items.map(i => (
                <div key={i.id} className="question-row">
                  <span className="question-text">{i.t}</span>
                  <div className="answer-buttons">
                    <button className={`answer-btn ${answers[i.id] === 'yes' ? 'selected yes' : ''}`}
                            onClick={() => handleAnswerChange(i.id, 'yes')}>YES</button>
                    <button className={`answer-btn ${answers[i.id] === 'no' ? 'selected no' : ''}`}
                            onClick={() => handleAnswerChange(i.id, 'no')}>NO</button>
                  </div>
                </div>
              ))}
            </section>
          ))}

          <div className="form-actions">
            <span className="progress-count">{answered} of {ALL_ITEMS.length} answered</span>
            <button className="secondary-btn" onClick={reset}><ArrowLeft size={20} /> Cancel</button>
            <button className="primary-btn" onClick={handleSubmit}><Target size={20} /> Generate Analysis</button>
          </div>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    if (!report) return null;
    const r = report.analysis;
    const S = Object.values(r.sections);
    const acct = r.totals.accounts, tot = r.totals.items;
    let nSec = 0;
    const N = () => ++nSec;

    return (
      <div className="report-view">
        <div className="report-container">

          <header className="report-header">
            <div className="report-title">
              <Brain size={32} />
              <div>
                <h1>NM Analysis Report</h1>
                <span className="report-meta">
                  {[report.patientInfo.surname, report.patientInfo.firstName]
                     .filter(Boolean).join(', ') || 'Demo'} ·{' '}
                  {new Date(report.timestamp).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className={`bof-output-badge ${r.bof.cls.toLowerCase().replace(/_/g, '-')}`}>
              {r.bof.cls}
              {r.bof.flag && <span className="safety-flag">⚠ {r.bof.flag}</span>}
            </div>
          </header>

          {r.sentinels.length > 0 && (
            <div className="sentinel-alert">
              <strong>⚠ SAFETY FLAG</strong> — sentinel item{r.sentinels.length > 1 ? 's' : ''}:{' '}
              {r.sentinels.map(s => `“${s.text}”`).join(' · ')}<br />
              Phase forced to STABILIZATION. The profile below is retained in full.
            </div>
          )}

          <div className="state-class">
            <div className="state-label">Output class</div>
            <div className="state-value">
              {r.bof.cls}
              {r.bof.flag && <span className="safety-flag"> ⚠ {r.bof.flag}</span>}
            </div>
          </div>
          <p className="note">
            Classification, not score. The framework holds that variation lies in which
            parts are down, not how far.
          </p>

          {report.assessmentCode && (
            <div className="assessment-code">
              <div className="code-label">Assessment code</div>
              <div className="code-value">{report.assessmentCode}</div>
              <p className="code-note">
                Nothing on this page is stored. The Analyzer keeps no record — print or
                save this report, and enter the code below into NM Rebuild, which holds
                the record for the clinician responsible for it.
              </p>
              <p className="code-note">
                The whole assessment, in eight characters. Every item is binary, so the
                39 answers are 39 bits. Entered into NM Rebuild this does not copy the
                reading — Rebuild runs the same engine on the same answers and arrives at
                the identical result. The prefix names the instrument: a code from a
                different version is refused rather than misread.
              </p>
            </div>
          )}

          <section className="report-section">
            <h2>{N()} · Section Profile</h2>
            <table className="data-table">
              <thead><tr><th>§</th><th>Section</th><th>Status</th><th>Concerning</th></tr></thead>
              <tbody>
                {S.map((s, n) => (
                  <tr key={s.id}>
                    <td>{n + 1}</td><td>{s.name}</td>
                    <td><Dot s={s.status} />{s.status}</td>
                    <td>{s.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>{N()} · Item Breakdown</h2>
            {S.filter(s => s.status !== 'GREEN').length === 0
              ? <p className="note">All sections green.</p>
              : S.filter(s => s.status !== 'GREEN').map(s => (
                  <div className="breakdown" key={s.id}>
                    <h4><Dot s={s.status} />{s.name} ({s.status})</h4>
                    <p><strong>Concerning</strong> · {s.concerning.join(' · ')}</p>
                    <p><strong>Intact</strong> · {s.intact.join(' · ') || 'none'}</p>
                  </div>
                ))}
          </section>

          <section className="report-section">
            <h2>{N()} · BOF Output Class</h2>
            <p className="note">BOF is a discipline against confabulation — not a calculation engine.</p>
            <div className={`bof-output-display ${r.bof.cls.toLowerCase().replace(/_/g, '-')}`}>
              <div className="bof-class">
                {r.bof.cls}{r.bof.flag && <span className="safety-flag"> ⚠ {r.bof.flag}</span>}
              </div>
              <p className="bof-description">{r.bof.d}</p>
            </div>
          </section>

          <section className="report-section">
            <h2>{N()} · The House — {r.house.filter(h => h.ok).length} of 7 answered</h2>
            <table className="data-table">
              <thead><tr><th>Station</th><th>Question</th><th>Answered</th></tr></thead>
              <tbody>
                {r.house.map(h => (
                  <tr key={h.key}>
                    <td>{h.station}</td><td>{h.q}</td>
                    <td><Dot s={h.ok ? 'GREEN' : 'RED'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>{N()} · Friction — {r.friction.filter(f => f.live).length} of 6 live</h2>
            <table className="data-table">
              <thead><tr><th>Pair</th><th></th><th>Live</th></tr></thead>
              <tbody>
                {r.friction.map(f => (
                  <tr key={f.id}>
                    <td>{f.pair}</td><td className="muted">{f.d}</td>
                    <td><Dot s={f.live ? 'RED' : 'GREEN'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>{N()} · The Loop</h2>
            <table className="data-table">
              <thead><tr><th>Stage</th><th>Running</th></tr></thead>
              <tbody>
                {r.loop.map(l => (
                  <tr key={l.key}>
                    <td>{l.stage}</td>
                    <td><Dot s={l.running ? 'GREEN' : 'RED'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>{N()} · Anchors &amp; Ground</h2>
            <p>
              Ground · <strong>{r.anchors.ground}</strong> &nbsp;|&nbsp;{' '}
<strong>{r.anchors.holding} of 4</strong> holding
            </p>
            <table className="data-table">
              <thead><tr><th>Anchor</th><th>Holding</th></tr></thead>
              <tbody>
                {r.anchors.set.map(x => (
                  <tr key={x.name}>
                    <td>{x.name}</td>
                    <td><Dot s={x.holding ? 'GREEN' : 'RED'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {r.annotations.length > 0 && (
            <section className="report-section">
              <h2>{N()} · Readings</h2>
              {r.annotations.map((t, i) => <div className="annotation" key={i}>{t}</div>)}
            </section>
          )}

          <section className="report-section">
            <h2>{N()} · Summary</h2>
            <p className="note">
              Discipline against confabulation: what supports movement against what constrains it.
            </p>
            <div className="bof-columns">
              <div className="bof-column pd">
                <h4><CheckCircle size={14} /> Navigable — supports movement</h4>
                <ul>{r.summary.nav.map((x, i) => <li key={i}>{x}</li>)}
                    {r.summary.nav.length === 0 && <li>none</li>}</ul>
              </div>
              <div className="bof-column pu">
                <h4><XCircle size={14} /> Constraining — limits movement</h4>
                <ul>{r.summary.con.map((x, i) => <li key={i}>{x}</li>)}
                    {r.summary.con.length === 0 && <li>none</li>}</ul>
              </div>
            </div>
          </section>


          <section className="report-section">
            <h2>{N()} · Phase Recommendation</h2>
            <div className={`rebuild-phase ${(PHASE_SLUG[r.phase] || '').replace(/_/g, '-')}`}>
              <strong>{r.phase}</strong>
            </div>
            <p className="note">
              Phase triggers are a clinical operationalization of the framework's structural
              logic. Regression is permitted when the profile warrants it.
            </p>
          </section>

          <section className="report-section">
            <h2>{N()} · What This Reading Does Not Establish</h2>
            <p className="limits">
              <strong>Cause.</strong> No item asks why, and no reading here implies one.<br />
              <strong>Duration.</strong> One session. This reads a state, not a trajectory —
              whether a contraction is protracted cannot be established from a single point.<br />
              <strong>Mood.</strong> No item asks about sadness or anhedonia. This profile may
              not register on a symptom checklist.<br />
              <strong>Account against reading.</strong> {acct} of {tot} items are accounts;{' '}
              {tot - acct} are events, arrangements or present readings. The gap between them
              is untested here.
            </p>
          </section>

          <footer className="report-footer">
            <p>NM Analyzer v4 — Navigational Mind Architecture</p>
            <p>© 2026 Dr. Olutoyese Oyelese</p>
            <p className="footer-quote">
              "The Analyzer detects state. It does not prescribe action."
            </p>
          </footer>

          <div className="report-actions no-print">
            {mode === 'demo' && (
              <button className="secondary-btn" onClick={() => setView('demo')}>
                <ArrowLeft size={20} /> Other profiles
              </button>
            )}
            <button onClick={handlePrint} className="primary-btn"><Printer size={20} /> Print / PDF</button>
            <button onClick={startNew} className="secondary-btn">
              <RefreshCw size={20} /> New assessment
            </button>
            <button onClick={exitAnalyzer} className="secondary-btn">
              <ArrowLeft size={20} /> {rebuildContext ? 'Exit to Rebuild' : 'Exit'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="app-container">
      <div className="site-bar no-print">
        <div className="site-bar-inner">
          <a href="https://navigationalmind.com">← navigationalmind.com</a>
        </div>
      </div>

      <header className="app-header no-print">
        <div className="header-left">
          <Brain size={32} className="logo-icon" />
          <div className="header-titles">
            <h1>NM Analyzer</h1>
            <span className="header-subtitle">v4 — Mind Machine State Detection</span>
          </div>
        </div>
        <div className="header-right">
          {rebuildContext && (
            <button onClick={returnToRebuild} className="rebuild-return-btn">← Rebuild</button>
          )}
          {mode === 'demo' && <span className="mode-badge">DEMO MODE</span>}
          {view === 'report' && (
            <button onClick={startNew} className="header-btn" title="New assessment">
              <RefreshCw size={18} />
            </button>
          )}
        </div>
      </header>

      <main className="app-main">
        {view === 'landing' && renderLanding()}
        {view === 'about' && renderAbout()}
        {view === 'demo' && renderDemo()}
        {view === 'input' && renderInput()}
        {view === 'report' && renderReport()}
      </main>
    </div>
  );
}

export default App;
